// CustomerRequests.jsx
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Alert,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { API_BASE_URL } from '../../constants/constants';
import { customerNoOf } from '../../components/CustomerReportShell';

import BackButton from '../../components/BackButton';
import { usePermissions } from '../../context/PermissionContext';

// Customers are shown by their public Customer No; merchant rows keep their existing id.
const displayIdOf = (row) => (row && row.customerType === 'customer' ? customerNoOf(row) : (row && row.customerId) || '—');

const CustomerRequests = () => {
  const [tabIndex, setTabIndex] = useState(0); // 0: Active / Solve, 1: History
  const [allSupports, setAllSupports] = useState([]);
  const [loading, setLoading] = useState(true);
  const { can } = usePermissions();
  const [userId, setUserId] = useState(null);
  const [userName, setUserName] = useState('');
  const [error, setError] = useState('');

  // Dropdown / Filter States
  const [districtList, setDistrictList] = useState([]);
  const [districtFilter, setDistrictFilter] = useState('all');
  const [pincodeFilter, setPincodeFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Solve Dialog State
  const [solveDialogOpen, setSolveDialogOpen] = useState(false);
  const [selectedSupport, setSelectedSupport] = useState(null);
  const [answerInput, setAnswerInput] = useState('');
  const [submittingAnswer, setSubmittingAnswer] = useState(false);

  // View Details Dialog State (for History)
  const [viewDialogOpen, setViewDialogOpen] = useState(false);

  const token = sessionStorage.getItem('token');

  // Decode token
  useEffect(() => {
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setUserId(decoded.userId);
        setUserName(`${decoded.firstName || ''} ${decoded.lastName || ''}`.trim() || 'Admin');
      } catch (e) {
        console.error('Failed to decode token:', e);
      }
    }
  }, [token]);

  // Fetch Districts Dropdown
  useEffect(() => {
    const fetchDistricts = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/districts`);
        if (res.status === 200 && res.data) {
          const list = Array.isArray(res.data) ? res.data : res.data.districts || [];
          setDistrictList(list);
        }
      } catch (err) {
        console.error('Error fetching districts:', err);
      }
    };
    fetchDistricts();
  }, []);

  // Fetch Support Requests
  const fetchSupportRequests = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API_BASE_URL}/customerSupports`, {
        params: {
          supportType: 'normalSupport',
          userId: userId
        },
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data && Array.isArray(response.data.supports)) {
        setAllSupports(response.data.supports);
      } else {
        setAllSupports([]);
      }
    } catch (err) {
      console.error('Error fetching support requests:', err);
      setError('Failed to fetch customer support requests.');
    } finally {
      setLoading(false);
    }
  }, [userId, token]);

  useEffect(() => {
    fetchSupportRequests();
  }, [fetchSupportRequests]);

  // Filter Data based on Tab & Controls
  const getFilteredData = () => {
    return allSupports.filter((item) => {
      // Tab 0: Active / Solve (status 1: New, status 2: In Progress)
      // Tab 1: History (status 3: Completed / Solved, status 4: Rejected)
      if (tabIndex === 0 && ![1, 2].includes(item.status)) return false;
      if (tabIndex === 1 && ![3, 4].includes(item.status)) return false;

      // District Filter
      if (districtFilter !== 'all') {
        if ((item.district || '').toLowerCase() !== districtFilter.toLowerCase()) {
          return false;
        }
      }

      // Pincode Filter
      if (pincodeFilter.trim()) {
        if (!String(item.pincode || '').includes(pincodeFilter.trim())) {
          return false;
        }
      }

      // Search Query Filter
      if (searchQuery.trim()) {
        const s = searchQuery.trim().toLowerCase();
        const matchName = (item.customerName || '').toLowerCase().includes(s);
        const matchId = String(displayIdOf(item)).toLowerCase().includes(s);
        const matchPhone = (item.customerMobile || item.supportToPhone || '').toLowerCase().includes(s);
        const matchCategory = (item.supportHead || '').toLowerCase().includes(s);
        const matchQc = (item.qpayCenterCustomId || '').toLowerCase().includes(s) || (item.qpayCenterName || '').toLowerCase().includes(s);

        if (!matchName && !matchId && !matchPhone && !matchCategory && !matchQc) {
          return false;
        }
      }

      return true;
    });
  };

  const filteredRows = getFilteredData();

  // Reset Filters
  const handleResetFilters = () => {
    setDistrictFilter('all');
    setPincodeFilter('');
    setSearchQuery('');
  };

  // CSV Export (permission gated)
  const handleExportCSV = () => {
    if (!can('support.customer.export')) return;
    if (filteredRows.length === 0) {
      alert('No data available to export.');
      return;
    }

    const isHistoryTab = tabIndex === 1;

    const headers = isHistoryTab
      ? [
          'SL No',
          'Date',
          'Customer/Merchant Name',
          'Customer/Merchant ID',
          'Issue Category',
          'Registered Contact Number',
          'Issue Description',
          'Answer / Resolution',
          'District',
          'Pincode'
        ]
      : [
          'SL No',
          'Date',
          'Customer/Merchant Name',
          'Customer/Merchant ID',
          'Issue Category',
          'Registered Contact Number',
          'Issue Description',
          'Status',
          'District',
          'Pincode'
        ];

    const rows = filteredRows.map((row, idx) => [
      idx + 1,
      new Date(row.createdAt).toLocaleString('en-IN'),
      `"${row.customerName || 'N/A'}"`,
      `"${displayIdOf(row)}"`,
      `"${row.supportHead || 'N/A'}"`,
      `"${row.customerMobile || row.supportToPhone || 'N/A'}"`,
      `"${(row.supportContent || '').replace(/"/g, '""')}"`,
      isHistoryTab
        ? `"${(row.answer || '').replace(/"/g, '""')}"`
        : row.status === 1 ? 'New' : 'In Progress',
      `"${row.district || 'N/A'}"`,
      `"${row.pincode || 'N/A'}"`
    ]);

    const csvContent =
      'data:text/csv;charset=utf-8,' +
      [headers.join(','), ...rows.map((e) => e.join(','))].join('\n');

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute(
      'download',
      `qpay_support_requests_${isHistoryTab ? 'history' : 'active'}_${new Date().toISOString().split('T')[0]}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Open Solve Dialog
  const handleOpenSolve = (row) => {
    if (!can('support.customer.solve')) return;
    setSelectedSupport(row);
    setAnswerInput(row.answer || '');
    setSolveDialogOpen(true);
  };

  // Open View History Details Dialog
  const handleOpenView = (row) => {
    setSelectedSupport(row);
    setViewDialogOpen(true);
  };

  // Submit Answer / Solve Support
  const handleSubmitAnswer = async () => {
    if (!can('support.customer.solve')) return;
    if (!answerInput.trim()) {
      alert('Please enter an answer/resolution before completing.');
      return;
    }
    if (!selectedSupport) return;

    setSubmittingAnswer(true);
    try {
      await axios.put(
        `${API_BASE_URL}/customerSupports/${selectedSupport._id}/status`,
        {
          status: 3, // Solved / Completed
          answer: answerInput.trim(),
          answeredBy: userId,
          answeredByName: userName
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      setSolveDialogOpen(false);
      setSelectedSupport(null);
      setAnswerInput('');
      fetchSupportRequests();
    } catch (err) {
      console.error('Error submitting answer:', err);
      alert('Failed to submit answer. Please try again.');
    } finally {
      setSubmittingAnswer(false);
    }
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#f4f6f8', minHeight: '100vh' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <BackButton />
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b', ml: 1 }}>
          Q-Pay Help-Desk
        </Typography>
      </Box>

      {/* Tabs Header */}
      <Paper sx={{ mb: 3, borderRadius: 2 }}>
        <Tabs
          value={tabIndex}
          onChange={(e, val) => setTabIndex(val)}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab
            label={
              <Typography sx={{ fontWeight: 700, textTransform: 'none', fontSize: '1rem' }}>
                Active Requests (View / Solve)
              </Typography>
            }
          />
          <Tab
            label={
              <Typography sx={{ fontWeight: 700, textTransform: 'none', fontSize: '1rem' }}>
                Solved Requests History
              </Typography>
            }
          />
        </Tabs>
      </Paper>

      {/* Filter Controls Box */}
      <Paper sx={{ p: 2.5, mb: 3, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
        <Grid container spacing={2} alignItems="center">
          {/* District Wise */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Filter by District</InputLabel>
              <Select
                value={districtFilter}
                label="Filter by District"
                onChange={(e) => setDistrictFilter(e.target.value)}
              >
                <MenuItem value="all">All Districts</MenuItem>
                {districtList.map((d) => {
                  const dName = d.districtName || d.name;
                  return (
                    <MenuItem key={d._id || dName} value={dName}>
                      {dName}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>

          {/* Pincode Filter */}
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Filter by Pincode"
              placeholder="e.g. 695001"
              value={pincodeFilter}
              onChange={(e) => setPincodeFilter(e.target.value)}
            />
          </Grid>

          {/* Search Query */}
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Search Name / ID / Category / Phone"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                endAdornment: <SearchIcon color="action" fontSize="small" />
              }}
            />
          </Grid>

          {/* Action Buttons */}
          <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={handleResetFilters}
              startIcon={<RefreshIcon />}
              fullWidth
              size="medium"
            >
              Reset
            </Button>
            {can('support.customer.export') && (
              <Button
                variant="contained"
                color="success"
                startIcon={<DownloadIcon />}
                onClick={handleExportCSV}
                fullWidth
                size="medium"
              >
                CSV
              </Button>
            )}
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Main Table */}
      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 700, color: '#1e293b', borderBottom: '1px solid #e2e8f0' }}>
                  SL No
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1e293b', borderBottom: '1px solid #e2e8f0' }}>
                  Customer / Merchant Name
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1e293b', borderBottom: '1px solid #e2e8f0' }}>
                  Customer / Merchant ID
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1e293b', borderBottom: '1px solid #e2e8f0' }}>
                  Issue Category
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1e293b', borderBottom: '1px solid #e2e8f0' }}>
                  Registered Contact Number
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1e293b', borderBottom: '1px solid #e2e8f0' }}>
                  Issue Description
                </TableCell>
                {tabIndex === 1 ? (
                  <TableCell sx={{ fontWeight: 700, color: '#1e293b', borderBottom: '1px solid #e2e8f0' }}>
                    Show Answers
                  </TableCell>
                ) : (
                  <TableCell align="center" sx={{ fontWeight: 700, color: '#1e293b', borderBottom: '1px solid #e2e8f0' }}>
                    Action / Solve
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3, color: '#64748b' }}>
                    No support requests found matching criteria.
                  </TableCell>
                </TableRow>
              ) : (
                filteredRows.map((row, idx) => (
                  <TableRow key={row._id} hover>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{row.customerName}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{displayIdOf(row)}</TableCell>
                    <TableCell sx={{ color: '#0284c7', fontWeight: 600 }}>{row.supportHead}</TableCell>
                    <TableCell>{row.customerMobile || row.supportToPhone || 'N/A'}</TableCell>
                    <TableCell sx={{ maxWidth: 220, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                      {row.supportContent}
                    </TableCell>
                    {tabIndex === 1 ? (
                      <TableCell sx={{ maxWidth: 260, whiteSpace: 'normal', color: '#059669', fontWeight: 500 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                          <Box>
                            {row.answer || (
                              <Chip
                                label={row.status === 4 ? 'Rejected' : 'Solved'}
                                color={row.status === 4 ? 'error' : 'success'}
                                size="small"
                              />
                            )}
                            {row.answeredByName && (
                              <Typography variant="caption" display="block" color="text.secondary">
                                By: {row.answeredByName}
                              </Typography>
                            )}
                          </Box>
                          <Button size="small" variant="outlined" onClick={() => handleOpenView(row)}>
                            View
                          </Button>
                        </Box>
                      </TableCell>
                    ) : (
                      <TableCell align="center">
                        {can('support.customer.solve') ? (
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            startIcon={<CheckCircleIcon />}
                            onClick={() => handleOpenSolve(row)}
                          >
                            Solve
                          </Button>
                        ) : (
                          <Typography variant="caption" color="text.secondary">-</Typography>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* Solve Dialog */}
      <Dialog open={solveDialogOpen} onClose={() => setSolveDialogOpen(false)} maxWidth="sm" fullWidth>
        {selectedSupport && (
          <>
            <DialogTitle sx={{ fontWeight: 700, backgroundColor: '#1e293b', color: 'white' }}>
              Solve Support Request #{displayIdOf(selectedSupport)}
            </DialogTitle>
            <DialogContent dividers sx={{ p: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Customer / Merchant Name:
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {selectedSupport.customerName}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Customer / Merchant ID:
                  </Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                    {displayIdOf(selectedSupport)}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Issue Category:
                  </Typography>
                  <Typography variant="body1" color="primary" fontWeight="bold">
                    {selectedSupport.supportHead}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Registered Contact Number:
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {selectedSupport.customerMobile || selectedSupport.supportToPhone || 'N/A'}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Issue Description:
                  </Typography>
                  <Paper sx={{ p: 1.5, backgroundColor: '#f8fafc', mt: 0.5, border: '1px solid #e2e8f0' }}>
                    <Typography variant="body2">{selectedSupport.supportContent}</Typography>
                  </Paper>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Enter Answer / Resolution"
                    placeholder="Provide detailed solution or response for the customer..."
                    value={answerInput}
                    onChange={(e) => setAnswerInput(e.target.value)}
                    sx={{ mt: 1 }}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={() => setSolveDialogOpen(false)} disabled={submittingAnswer}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={handleSubmitAnswer}
                disabled={submittingAnswer || !answerInput.trim()}
                startIcon={submittingAnswer ? <CircularProgress size={20} /> : <CheckCircleIcon />}
              >
                {submittingAnswer ? 'Submitting...' : 'Submit & Complete'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* View Dialog (For Solved History) */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="sm" fullWidth>
        {selectedSupport && (
          <>
            <DialogTitle sx={{ fontWeight: 700, backgroundColor: '#1e293b', color: 'white' }}>
              Support Request History Details
            </DialogTitle>
            <DialogContent dividers sx={{ p: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Customer / Merchant Name:
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {selectedSupport.customerName}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Customer / Merchant ID:
                  </Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                    {displayIdOf(selectedSupport)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Issue Category:
                  </Typography>
                  <Typography variant="body1" color="primary" fontWeight="bold">
                    {selectedSupport.supportHead}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Registered Contact Number:
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {selectedSupport.customerMobile || selectedSupport.supportToPhone || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Issue Description:
                  </Typography>
                  <Paper sx={{ p: 1.5, backgroundColor: '#f8fafc', mt: 0.5 }}>
                    <Typography variant="body2">{selectedSupport.supportContent}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Answer / Resolution Given:
                  </Typography>
                  <Paper sx={{ p: 1.5, backgroundColor: '#ecfdf5', mt: 0.5, border: '1px solid #a7f3d0' }}>
                    <Typography variant="body2" color="#047857" fontWeight={600}>
                      {selectedSupport.answer || 'No answer recorded.'}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default CustomerRequests;
